import React from "react";
import { Navigate, Outlet } from "react-router-dom";

/**
 * Checks whether a JWT token is structurally valid and not expired.
 * This is a CLIENT-SIDE check only — it does NOT verify the signature
 * (that's the backend's job). It prevents rendering protected pages
 * with obviously bad or expired tokens, improving UX.
 */
export function isTokenValid(token) {
  if (!token) return false;

  try {
    // JWT structure: header.payload.signature
    const parts = token.split(".");
    if (parts.length !== 3) return false;

    // Convert Base64URL string to standard Base64 before atob()
    let base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4) {
      base64 += "=";
    }

    // Decode the payload
    const payload = JSON.parse(atob(base64));

    // Check expiry: `exp` is in seconds, Date.now() is in milliseconds
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      // Token has expired — clean up storage
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      sessionStorage.removeItem("authUser");
      return false;
    }

    return true;
  } catch (e) {
    console.warn("[PrivateRoute] Token decoding failed:", e);
    // Clean up malformed token
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    sessionStorage.removeItem("authUser");
    return false;
  }
}

/**
 * PrivateRoute — wraps protected sections of the app.
 * Validates the JWT token in localStorage. If absent, expired, or
 * corrupted, redirects to /auth/login immediately without rendering
 * the protected page.
 */
function PrivateRoute({ children }) {
  const token = localStorage.getItem("token");

  if (!isTokenValid(token)) {
    return <Navigate to="/auth/login" replace />;
  }

  return children ?? <Outlet />;
}

export default PrivateRoute;
